import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { NumberFieldDto, ProductGroupBidDto, ProductGroupDto } from "../../autogen/bff-api";
import type { Product, ProductGroup, ProductTemplateField } from "../types";
import { ProductTableRow } from "./ProductTableRow";
import { displayProductGroupPrice, displaySumOfProductGroupPrices } from "./productUtils";

// Adapts a V2 product-group definition plus one bid's product answers into the V1 domain shape,
// so the delivered-bid presentation (ProductTableRow's clamp cells, group and total sums) renders
// identically for V1 and V2 bids. Fields match by name — the V2 convention throughout.
const withNumericValue = (field: NumberFieldDto): NumberFieldDto => ({
  ...field,
  value: field.value == null ? field.value : Number(field.value),
});

export const toBidProductGroups = (
  groups: ProductGroupDto[],
  bidProducts: ProductGroupBidDto[],
): ProductGroup[] =>
  groups
    .map((group) => {
      const groupBid = bidProducts.find((bid) => bid.productGroupId === group.id);
      const supplierFields = group.productFields.filter((field) => field.populatedBy === "Supplier");
      const products: Product[] = group.products.map((product) => {
        const productBid = groupBid?.productBids.find((bid) => bid.productId === product.id);
        return {
          id: product.id,
          quantity: withNumericValue(product.quantity),
          unitPrice: productBid?.unitPrice && withNumericValue(productBid.unitPrice),
          productFields: [
            ...product.productFields.filter((field) => field.populatedBy === "Buyer"),
            ...supplierFields.map((field) => ({
              ...field,
              value: productBid?.productFields.find((answer) => answer.name === field.name)?.value,
            })),
          ],
        };
      });
      return {
        id: group.id,
        name: group.name,
        productFields: group.productFields as unknown as ProductTemplateField[],
        products,
      };
    })
    .filter((group) => group.products.length > 0);

export const BidProductTables = ({ groups }: { groups: ProductGroup[] }) => {
  const { t } = useTranslation();

  if (groups.length === 0) return null;

  return (
    <Box>
      <Flex justifyContent="center" width="full" pb="6">
        <Flex
          flexDirection="column"
          shadow="sm"
          width="fit-content"
          border="2px solid"
          borderColor="blue.100"
          rounded="md"
          p="4"
        >
          <Text fontSize="3xl" fontWeight="semibold" textAlign="center">
            {displaySumOfProductGroupPrices(groups)}
          </Text>
          <Text>{t("Sum of product prices")}</Text>
        </Flex>
      </Flex>
      {groups.map((group) => (
        <Box key={group.id} border="1px solid" borderColor="teal.100" rounded="lg" mt="5" pb="2.5">
          <Text as="h2" fontWeight="semibold" p="4">
            {group.name}
          </Text>
          <Box overflowX="auto">
            <Table variant="simple" overflowX="auto" overflowY="visible" size="sm">
              <Thead>
                <Tr>
                  {group.productFields
                    .filter((field) => field.populatedBy === "Buyer")
                    .map((field) => (
                      <Th key={field.id}>{field.name}</Th>
                    ))}
                  <Th>{t("Quantity")}</Th>
                  <Th>{t("Unit Price")}</Th>
                  <Th>{t("Total Price")}</Th>
                  {group.productFields
                    .filter((field) => field.populatedBy === "Supplier")
                    .map((field) => (
                      <Th key={field.id}>{field.name}</Th>
                    ))}
                </Tr>
              </Thead>
              <Tbody>
                {group.products?.map((product) => (
                  <ProductTableRow key={product.id} product={product} templateFields={group.productFields} />
                ))}
                <Tr fontWeight="bold">
                  {group.productFields
                    .filter((field) => field.populatedBy === "Buyer")
                    .map((field, index) => (
                      <Td key={field.id}>{index === 0 ? "SUM" : ""}</Td>
                    ))}
                  <Td></Td>
                  <Td></Td>
                  <Td textAlign="center" verticalAlign="top">
                    {displayProductGroupPrice(group)}
                  </Td>
                  {group.productFields
                    .filter((field) => field.populatedBy === "Supplier")
                    .map((field) => (
                      <Td key={field.id}></Td>
                    ))}
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
